<template>
  <v-container>
    <v-layout>
      <v-flex>
        <div class="custom-login">
          <ValidationObserver
            ref="customLoginForm"
            v-slot="{ invalid }"
          >
            <v-form @submit.prevent="submitForm">
              <template v-for="(component, index) in components">
                <component
                  :is="getComponentType(component.type)"
                  :key="index"
                  :component="component"
                  v-model="formFields[component.name]"
                  :form-invalid="
                    invalid || formButtons[component.action]
                  "
                  :loading="formButtons[component.action]"
                  :button-disabled="formButtons[component.action]"
                  @field-registered="addKeyToState"
                  @button-registered="addLoadingButtonKey"
                  @action-triggered="handleAction"
                />
              </template>
            </v-form>
          </ValidationObserver>
        </div>
      </v-flex>
    </v-layout>
    <v-dialog v-model="showDialog" max-width="340px">
      <v-card>
        <v-card-title>
          <h3>{{ dialogTitle }}</h3>
        </v-card-title>
        <v-card-text>
          <p>{{ dialogMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="closeDialog"
          >
            {{ $t('smsSignIn.errorDialogButton') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import _ from 'lodash'
import {mapGetters, mapActions, mapState} from 'vuex'
import genericComponents from '@/mixins/genericComponents'
import {email} from 'vee-validate/dist/rules';

export default {
  mixins: [genericComponents],
  data() {
    return {
      formFields: {},
      formButtons: {},
      showDialog: false,
      dialogTitle: '',
      dialogMessage: ''
    }
  },
  computed: {
    ...mapGetters(['authSettings', 'webviewSettings', 'isAuthenticated']),
    ...mapState(['firebaseResult']),
    authenticatorName() {
      const authenticators = _.get(this.authSettings, 'authenticators', [])

      const auth = authenticators.find((authenticator) => {
        return _.get(authenticator, 'identification_field', '') === 'customer_id'
      })

      return auth ? _.get(auth, 'name', '') : ''
    },
    isFirebaseSmsAuthEnabled() {
      return _.get(
        this.authSettings, 'enable_firebase_phone_auth', false
      )
    },
    isOwnSmsAuthEnabled() {
      return _.get(this.authSettings, 'enable_sms_login', false)
    },
  },
  mounted() {
    const screenKey = this.$route.params.screen || _.get(this.webviewSettings, 'custom_login_screen', null)

    if (screenKey) {
      // TODO: Use proper extension settings
      this.components = _.get(
        this.authSettings,
        `webview.custom_authentication_screens.${screenKey}.components`,
        []
      )
    }
  },
  methods: {
    ...mapActions(['customerLogin', 'verifyOTP', 'verifyEmailOTP', 'firebaseVerifyOTP']),
    addKeyToState(inputName) {
      if (inputName && !_.has(this.formFields, inputName)) {
        this.$set(this.formFields, inputName, '')
      }
    },
    addLoadingButtonKey(actionId) {
      if (actionId && !_.has(this.formButtons, actionId)) {
        this.$set(this.formButtons, actionId, false)
      }
    },
    setLoadingButtonState(key, value) {
      if (key && _.has(this.formButtons, key)) {
        this.$set(this.formButtons, key, value)
      }
    },
    handleAction(action) {
      if (this[action]) {
        this[action]()
      }
    },
    submitForm() {
      this.$refs.customLoginForm.validate().then((success) => {
        if (!success) {
          return
        }

        this.loginCustomer()
      })
    },
    async loginCustomer() {
      const buttonKey = 'loginCustomer'

      try {
        this.setLoadingButtonState(buttonKey, true)

        await this.customerLogin({
          authName: this.authenticatorName,
          payload: {
            ...this.formFields,
            ...{
              accept_tos: true
            }
          }
        })

        this.setLoadingButtonState(buttonKey, false)

        if (this.isAuthenticated) {
          if (this.$store.getters.isOpenIdProvider) {
            this.$router.push({name: 'openid'});
          } else {
            this.$router.push({name: 'home'})
          }
        }
      } catch (error) {
        this.setLoadingButtonState(buttonKey, false)
        this.openDialog(
          this.$t('customLogin.customerLoginErrorTitle'),
          this.$t('customLogin.customerLoginErrorText')
        )
      }
    },
    async createAccount() {
      const buttonKey = 'createAccount'

      try {
        this.setLoadingButtonState(buttonKey, true)
        const isEmailIdentified = email.validate(this.$route.params.identifier)

        if (isEmailIdentified) {
          await this.verifyEmailOTP({
            email: this.$route.params.identifier,
            verificationCode: this.$route.params.verificationCode,
            skipVerify: true,
            loginOnly: false
          })
        } else {
          if (this.isFirebaseSmsAuthEnabled) {
            await this.firebaseVerifyOTP({
              confirmationResult: this.firebaseResult,
              verificationCode: this.$route.params.verificationCode,
              skipVerify: true
            })
          } else if (this.isOwnSmsAuthEnabled) {
            await this.verifyOTP({
              phoneNumber: this.$route.params.identifier,
              verificationCode: this.$route.params.verificationCode,
              skipVerify: true,
              loginOnly: false
            })
          }
        }


        this.setLoadingButtonState(buttonKey, false)

        if (this.isAuthenticated) {
          if (this.$store.getters.isOpenIdProvider) {
            this.$router.push({name: 'openid'});
          } else {
            this.$router.push({name: 'home'})
          }
        }
      } catch (error) {
        this.setLoadingButtonState(buttonKey, false)
        this.openDialog(
          this.$t('customLogin.createAccountErrorTitle'),
          this.$t('customLogin.createAccountErrorText')
        )
      }
    },
    openDialog(title, message) {
      this.dialogTitle = title
      this.dialogMessage = message
      this.showDialog = true
    },
    closeDialog() {
      this.showDialog = false
      this.dialogTitle = ''
      this.dialogMessage = ''
    }
  },
}
</script>

<style lang="scss" scoped>
.custom-login {
  max-width: 360px;
  margin: 0 auto;
}
</style>
